import { useState } from 'react';
import type { SoloRunState, StoryPack, Track } from '@plotjack/core';
import { CARD_TYPE_RISK, endingForTrack, endingTarget, trackLabel } from '@plotjack/core';

interface Props {
  state: SoloRunState;
  story: StoryPack;
  /** recent deltas to flash next to a meter */
  pulse?: Partial<Record<Track | 'dreadMeter', number>>;
}

const HOW: Record<string, string> = {
  hope: 'Keepsake cards',
  chaos: 'Chaos cards',
  fate: 'Twist and Rule-breaker cards',
};

/** Dread, Ink and the three ending meters, labelled with the ending each one leads to. */
export function EndingMeters({ state, story, pulse }: Props) {
  const [help, setHelp] = useState(false);
  const chosen = story.endings.find((e) => e.id === state.chosenEndingId);
  return (
    <header className="hud" aria-label="Run status">
      <div className="hud-item hud-item--dread">
        <span className="hud-label">Dread</span>
        <meter className="dread-meter" min={0} max={10} low={4} high={7} optimum={0} value={state.dread} aria-label={`Dread ${state.dread} of 10`}>
          {state.dread}
        </meter>
        <span className="hud-value">{state.dread}/10</span>
        {pulse?.dreadMeter ? <span className={`hud-pulse ${pulse.dreadMeter > 0 ? 'hud-pulse--bad' : 'hud-pulse--good'}`}>{pulse.dreadMeter > 0 ? '+' : ''}{pulse.dreadMeter}</span> : null}
        <span className="hud-sub">At 10, the Archivist wins</span>
      </div>
      <div className="hud-item">
        <span className="hud-label">Ink</span>
        <span className="hud-value">{state.energy}/{state.maxEnergy}</span>
        <span className="hud-sub">Cards cost Ink</span>
      </div>
      <div className="hud-endings" aria-label="Ending meters">
        {(['hope', 'chaos', 'fate'] as Track[]).map((t) => {
          // the chosen ending's own target on its track (Narrator Quits shares the chaos meter)
          const isChosen = chosen?.track === t;
          const e = isChosen ? chosen : endingForTrack(story, t);
          const target = e ? endingTarget(e, state) : 8;
          const v = state.tracks[t] ?? 0;
          return (
            <div key={t} className={`ending-meter ${isChosen ? 'ending-meter--chosen' : ''}`} data-track={t}>
              <span className="ending-meter__label">
                {trackLabel(story, t)}
                {isChosen && <span className="ending-meter__tag">your ending</span>}
              </span>
              <span className="ending-meter__bar" aria-hidden="true">
                <span style={{ width: `${Math.min(100, (v / target) * 100)}%` }} />
              </span>
              <span className="ending-meter__value">
                {v}/{target}
                {pulse?.[t] ? <span className="hud-pulse hud-pulse--good">+{pulse[t]}</span> : null}
              </span>
            </div>
          );
        })}
        <button type="button" className="btn btn-ghost btn-small hud-help" aria-expanded={help} onClick={() => setHelp((h) => !h)}>
          How cards move these
        </button>
        {help && (
          <div className="hud-help-pop" role="note">
            <p>Reach your ending's number by the last scene to win it. Miss it and another ending decides. Longer runs need bigger numbers: the full book asks three times what one chapter does.</p>
            <ul>
              {(['hope', 'chaos', 'fate'] as Track[]).map((t) => (
                <li key={t}>
                  <strong>{trackLabel(story, t)}:</strong> {HOW[t]} push it up.
                </li>
              ))}
              <li>
                <strong>Dread:</strong> when a scene ends, the narrator's plan costs Dread +2 if you played nothing and +1 if you played one card. Two or more cards bend it and a Counter blocks it (Dread -1), so neither costs anything. Chaos cards add Dread of their own.
              </li>
            </ul>
            <p className="hud-help-sub">Every card type has its own risk:</p>
            <ul>
              {['keepsake', 'twist', 'chaos', 'rule_breaker', 'counter'].map((k) => (
                <li key={k}>{CARD_TYPE_RISK[k]}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </header>
  );
}
